"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface ProductItem {
  id: string;
  name: Record<Lang, string>;
  desc: Record<Lang, string>;
  to: string;
  image: string;
  alt: Record<Lang, string>;
}
const products: ProductItem[] = [{
  id: "woven-wire-mesh",
  name: {
    en: "Woven Wire Mesh",
    zh: "精密编织丝网",
    es: "Malla de alambre tejida",
    de: "Drahtgewebe",
    fr: "Toile métallique tissée",
    pt: "Malha de arame tecida"
  },
  desc: {
    en: "Square and Dutch weaves for precise particle retention and uniform flow.",
    zh: "平纹、斜纹与密纹编织结构，用于微米级颗粒截留与均匀流体通量。",
    es: "Tejidos cuadrados y holandeses para retención precisa y flujo uniforme.",
    de: "Quadrat- und Tressenbindung für präzise Partikelrückhaltung und gleichmäßigen Durchfluss.",
    fr: "Tissages carrés et reps pour une rétention précise et un écoulement régulier.",
    pt: "Tecelagens quadradas e holandesas para retenção precisa e fluxo uniforme."
  },
  to: "/WovenWireMeshPage",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/woven_wire_mesh_macro.webp",
  alt: {
    en: "Stainless steel woven wire mesh geometry macro view",
    zh: "精密不锈钢编织丝网几何结构微距特写",
    es: "Vista macro de la geometría de malla de alambre tejida en acero inoxidable",
    de: "Makroansicht der Geometrie von Edelstahl-Drahtgewebe",
    fr: "Vue macro de la géométrie de toile métallique tissée en acier inoxydable",
    pt: "Visão macro da geometria de malha de arame tecida em aço inoxidável"
  }
}, {
  id: "sintered-wire-mesh",
  name: {
    en: "Sintered Wire Mesh",
    zh: "烧结金属网与多孔板",
    es: "Malla de alambre sinterizada",
    de: "Sinterdrahtgewebe",
    fr: "Toile métallique frittée",
    pt: "Malha de arame sinterizada"
  },
  desc: {
    en: "Diffusion-bonded multi-layer laminates with high mechanical strength and stable pore geometry.",
    zh: "多层真空扩散焊接成型，具备高机械强度与稳固的孔径结构。",
    es: "Laminados multicapa unidos por difusión con alta resistencia mecánica y poros estables.",
    de: "Diffusionsverschweißte Mehrlagen-Laminate mit hoher Festigkeit und stabiler Porengeometrie.",
    fr: "Stratifiés multicouches soudés par diffusion offrant haute résistance et pores stables.",
    pt: "Laminados multicamadas unidos por difusão com alta resistência e poros estáveis."
  },
  to: "/SinteredWireMeshPage",
  image: "https://mutualfilter.com/wp-content/uploads/2026/09/304-316L-SS-Stainless-Steel-Sintered-Wire-Mesh-Filter-Disc.jpg?v=gwqusj7w1kt",
  alt: {
    en: "Multi-layer diffusion bonded sintered wire mesh plate",
    zh: "多层真空扩散焊接烧结金属网板",
    es: "Placa de malla de alambre sinterizada multicapa unida por difusión",
    de: "Mehrlagige diffusionsverschweißte Sinterdrahtgewebeplatte",
    fr: "Plaque de toile métallique frittée multicouche soudée par diffusion",
    pt: "Placa de malha de arame sinterizada multicamada unida por difusão"
  }
}, {
  id: "filter-components",
  name: {
    en: "Filter Components",
    zh: "精密滤芯与过滤组件",
    es: "Componentes de filtración",
    de: "Filterkomponenten",
    fr: "Composants de filtration",
    pt: "Componentes de filtração"
  },
  desc: {
    en: "Cylindrical elements, pleated cartridges and stamped discs built to tight OEM tolerances.",
    zh: "定制加工滤筒、折叠滤芯与冲压网片，符合严格的OEM制造公差。",
    es: "Elementos cilíndricos, cartuchos plisados y discos troquelados según tolerancias OEM.",
    de: "Zylinderfilter, Plisseepatronen und Stanzsiebe nach engen OEM-Toleranzen.",
    fr: "Éléments cylindriques, cartouches plissées et disques emboutis selon tolérances OEM.",
    pt: "Elementos cilíndricos, cartuchos plissados e discos estampados com tolerâncias OEM."
  },
  to: "/FilterComponentsPage",
  image: "https://mutualfilter.com/wp-content/uploads/2026/09/Pleated-Filters2-scaled1.webp?v=f32b479cldh",
  alt: {
    en: "Industrial stainless steel cylindrical filter elements and assemblies",
    zh: "工业级不锈钢过滤筒与精密滤芯组件",
    es: "Elementos filtrantes cilíndricos y conjuntos de filtración industrial en acero inoxidable",
    de: "Industrielle zylindrische Filterelemente und Baugruppen aus Edelstahl",
    fr: "Éléments filtrants cylindriques industriels et assemblages en acier inoxydable",
    pt: "Elementos filtrantes cilíndricos e montagens industriais em aço inoxidável"
  }
}, {
  id: "wedge-wire-screens",
  name: {
    en: "Wedge Wire Screens",
    zh: "楔形条缝筛网",
    es: "Tamices de alambre en cuña",
    de: "Spaltsiebe (Keilspaltsiebe)",
    fr: "Crépines à fil profilé",
    pt: "Telas de arame em cunha"
  },
  desc: {
    en: "Continuous-slot welded profile wire screens for high-capacity solid-liquid separation.",
    zh: "连续缝隙全焊接条缝筛结构，适用于大通量固液分离与分级工况。",
    es: "Tamices de ranura continua soldada para separación sólido-líquido de alta capacidad.",
    de: "Geschweißte Spaltsiebe mit kontinuierlichem Spalt für Hochleistungs-Fest-Flüssig-Trennung.",
    fr: "Crépines soudées à fente continue pour la séparation solide-liquide à fort débit.",
    pt: "Telas de arame perfilado soldado de fenda contínua para separação sólido-líquido."
  },
  to: "/WedgeWireScreenPage",
  image: "https://mutualfilter.com/wp-content/uploads/2026/09/flat-weged-wire-screen.webp?v=4caj9h83rmy",
  alt: {
    en: "Welded profile wedge wire screen cylinder construction",
    zh: "全焊接V型精密楔形丝条缝筛管结构",
    es: "Construcción cilíndrica de tamiz de alambre en cuña de perfil soldado",
    de: "Konstruktion eines geschweißten Spaltsiebrohrs aus Keildrahtprofilen",
    fr: "Construction cylindrique de crépine à fil profilé soudé",
    pt: "Construção cilíndrica de tela de arame em cunha de perfil soldado"
  }
}];
export default function Coreproducts() {
  const {
    lang
  } = useLanguage();
  const eyebrow = pick(lang, {
    en: "PRODUCT PORTFOLIO",
    zh: "核心产品系列",
    es: "CARTERA DE PRODUCTOS",
    de: "PRODUKTPORTFOLIO",
    fr: "PORTEFEUILLE DE PRODUITS",
    pt: "PORTFÓLIO DE PRODUTOS"
  });
  const heading = pick(lang, {
    en: "Core Product Range",
    zh: "四大核心系列",
    es: "Gama de productos principales",
    de: "Kernproduktprogramm",
    fr: "Gammes de produits principales",
    pt: "Linha principal de produtos"
  });
  const leadText = pick(lang, {
    en: "Engineered filtration media tuned to your required alloy, aperture and geometry.",
    zh: "根据所需合金材质、微米孔径和结构几何量身定制的过滤介质。",
    es: "Medios de filtración diseñados según la aleación, abertura y geometría requeridas.",
    de: "Filtrationsmedien, präzise ausgelegt auf Werkstoff, Maschenweite und Geometrie.",
    fr: "Médias filtrants adaptés à votre alliage, votre seuil de coupure et votre géométrie.",
    pt: "Meios de filtração projetados conforme a liga, a abertura e a geometria necessárias."
  });
  const viewAllLink = pick(lang, {
    en: "View all products",
    zh: "查看全部产品",
    es: "Ver todos los productos",
    de: "Alle Produkte ansehen",
    fr: "Voir tous les produits",
    pt: "Ver todos os produtos"
  });
  const cardAction = pick(lang, {
    en: "Explore specifications",
    zh: "查看产品详情",
    es: "Explorar especificaciones",
    de: "Spezifikationen ansehen",
    fr: "Découvrir les spécifications",
    pt: "Explorar especificações"
  });
  return <section id="coreproducts" data-nav="light" className="bg-background mf-section border-b border-border">
      <div className="mf-container">
        {/* Section Header */}
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 lg:mb-16">
            <div className="max-w-2xl">
              <span className="text-xs uppercase tracking-widest font-mono text-muted-foreground block mb-3">
                {eyebrow}
              </span>
              <h2 className="mf-h2 text-foreground">
                {heading}
              </h2>
              <p className="mt-3 text-base text-muted-foreground leading-relaxed">
                {leadText}
              </p>
            </div>
            <div className="shrink-0">
              <Link to="/ProductsPage" className="group inline-flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors">
                <span>{viewAllLink}</span>
                <ArrowRight className="size-4 shrink-0 mf-arrow-nudge text-foreground group-hover:text-primary transition-colors" />
              </Link>
            </div>
          </div>
        </Reveal>

        {/* 2x2 Product Card Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
          {products.map((item, i) => <Reveal key={item.id} delay={Math.min(i * 80, 300)}>
              <Link to={item.to} className="group flex flex-col h-full bg-card border border-border rounded-lg overflow-hidden mf-card-hover">
                {/* Big Product Image */}
                <div className="mf-card-media mf-ratio-card mf-img-zoom">
                  <img src={item.image} alt={item.alt[lang] ?? item.alt.en} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                </div>

                {/* Card Content */}
                <div className="flex flex-col flex-1 p-6 lg:p-8">
                  <h3 className="mf-h3 text-foreground mf-card-title transition-colors mb-3">
                    {item.name[lang] ?? item.name.en}
                  </h3>

                  <p className="text-base text-muted-foreground leading-relaxed mb-6 flex-1">
                    {item.desc[lang] ?? item.desc.en}
                  </p>

                  <div className="flex items-center gap-2 text-sm font-semibold text-primary mt-auto">
                    <span>{cardAction}</span>
                    <ArrowRight className="size-4 shrink-0 mf-arrow-nudge" />
                  </div>
                </div>
              </Link>
            </Reveal>)}
        </div>
      </div>
    </section>;
}